import { useEffect, useRef, useState } from 'react';
import { InputNumber, Slider } from 'antd';
import type { InputNumberRef } from 'rc-input-number';
import {useViewerStore} from "../../../store/store";
import { AlertType } from '../../../@types/enums';

type SliderInputProps = {
    settings: any,
    onChange: any,
    value: number,
    unit?: string,
    onChangeComplete?: (newValue: number, oldValue: number) => void,
    sliderMarks?: any,
    hideInput?: boolean,
    disabled?: boolean,
    included?: boolean,
}

const SliderInput = (props: SliderInputProps) => {
    const {settings, onChange, value, unit, onChangeComplete, sliderMarks, hideInput, disabled, included} = props;

    const sceneViewer = useViewerStore(state => state.sceneViewer);

    const inputRef = useRef<InputNumberRef>(null);
    const [inputValue, setInputValue] = useState(Number(value));
    const [dragging, setDragging] = useState(false);
    const startValueRef = useRef(Number(value));

    const onChangeCompleteHandler = (v?: number) => {
        setDragging(false);
        const newValue = v !== undefined ? Number(v) : inputValue;
        const oldValue = startValueRef.current;
        if (inputValue === null || oldValue === newValue) return

        onChangeComplete?.(newValue, oldValue);
        startValueRef.current = newValue; 
        setInputValue(newValue);
        inputRef.current?.blur();
    };

    const onSliderChange = (v: number) => {
        if (!dragging) {
            setDragging(true);
            startValueRef.current = Number(value);
        }
        setInputValue(v);
        onChange(v);
    };

    const inputChangeHandler = (newValue: number | null) => {
        if(newValue) setInputValue(newValue);
    };

    const inputOnChangeCompleteHandler = () => {
        if (inputValue < settings.min || inputValue > settings.max) {
            sceneViewer.viewerAlert({
                title: 'Value Error',
                message: `Cannot set value outside of range ${settings.min} - ${settings.max} ${unit ? ` ${unit}` : ''}`,
                messageType: AlertType.error,
                show: true,
                isModal: false,
                isCloseAfterDisplay: true,
                autoCloseSeconds : 5000
            })
            setInputValue(value);
        } else {
            const originalValue = startValueRef.current;
            onChange(inputValue);
            // onChangeCompleteHandler();
            onChangeComplete?.(inputValue, originalValue);
            startValueRef.current = inputValue;
            inputRef.current?.blur();
        }
    };

    useEffect(() => {
        if (!dragging) {
            setInputValue(Number(value));
            startValueRef.current = Number(value);
        }
    }, [value, dragging]);;

    return (
        <div className="slider-input-container">
            <div className="slider-input-range" style={hideInput ? { width: '100%' } : undefined}>
                <Slider
                    min={settings.min}
                    max={settings.max}
                    step={settings.step}
                    included={included}
                    onChange={onSliderChange}
                    disabled = {disabled}
                    onChangeComplete={onChangeCompleteHandler}
                    tooltip={{
                        formatter: null,
                    }}
                    value={value}
                    marks={sliderMarks}
                    style={hideInput ? { marginBottom: '20px' } : undefined}
                />
            </div>
            {!hideInput && (
                <div className="slider-input-value">
                    <InputNumber
                        ref={inputRef}
                        step={settings.step}
                        value={inputValue}
                        onChange={inputChangeHandler}
                        onPressEnter={inputOnChangeCompleteHandler}
                        onBlur={inputOnChangeCompleteHandler}
                        onKeyDown={
                        (e) => {
                            if(e.key === 'Escape') {
                                setInputValue(value);
                                inputRef.current?.blur();
                            }
                        }
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default SliderInput;